import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaUser, FaPhone, FaBriefcase, FaCalendar, FaPlus, FaTimes, FaSave, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    profile_occupation: '',
    date_of_birth: '',
    age: '',
    contact: '',
    spouse_name: '',
    total_dependents: 0,
    children: [],
    // Advisor branding
    advisor_name: user?.name || '',
    advisor_designation: 'Certified Financial Planner (CFP)',
    advisor_company: '',
    advisor_phone: user?.phone || '',
    advisor_email: user?.email || '',
    advisor_website: ''
  });

  const [prefillData, setPrefillData] = useState(null);
  const [showPrefillBanner, setShowPrefillBanner] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchClient();
    }
    
    // Check for prefill data from "Copy to Arth Gurukul" button
    checkForPrefillData();
  }, [id]);

  const checkForPrefillData = () => {
    const storedData = localStorage.getItem('arthKundliPrefillData');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        setPrefillData(data);
        setShowPrefillBanner(true);
        
        // Auto-fill matching fields
        setFormData(prev => ({
          ...prev,
          name: data.name || prev.name,
          profile_occupation: data.profile_occupation || prev.profile_occupation,
          date_of_birth: data.date_of_birth || prev.date_of_birth,
          age: data.age || prev.age,
          contact: data.contact || prev.contact,
          spouse_name: data.spouse_name || prev.spouse_name,
          total_dependents: data.total_dependents || prev.total_dependents,
          children: data.children && data.children.length > 0 ? data.children : prev.children,
        }));
        
        // Clear the localStorage after reading
        localStorage.removeItem('arthKundliPrefillData');
      } catch (e) {
      }
    }
  };

  const fetchClient = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/clients/${id}`);
      setFormData(response.data);
    } catch (error) {
      setError('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Auto-calculate age from date of birth
    if (name === 'date_of_birth' && value) {
      const dob = new Date(value);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear();
      setFormData(prev => ({ ...prev, age }));
    }
  };

  const addChild = () => {
    setFormData({
      ...formData,
      children: [
        ...formData.children,
        {
          id: `temp-${Date.now()}`,
          name: '',
          date_of_birth: '',
          age: 0,
          class_grade: ''
        }
      ]
    });
  };

  const removeChild = (index) => {
    const newChildren = formData.children.filter((_, i) => i !== index);
    setFormData({ ...formData, children: newChildren });
  };

  const updateChild = (index, field, value) => {
    const newChildren = [...formData.children];
    newChildren[index][field] = value;

    // Auto-calculate child's age
    if (field === 'date_of_birth' && value) {
      const dob = new Date(value);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear();
      newChildren[index].age = age;
    }

    setFormData({ ...formData, children: newChildren });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const clientData = {
        ...formData,
        user_id: formData.user_id || `user-${Date.now()}`,
        advisor_id: user.id,
        total_dependents: formData.children.length
      };

      if (isEdit) {
        await axios.put(`${API}/clients/${id}`, clientData);
      } else {
        await axios.post(`${API}/clients`, clientData);
      }

      navigate('/dashboard/clients');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <FaSpinner className="animate-spin text-[#FFD700]" size={48} />
      </div>
    );
  }

  // Format currency for display
  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {isEdit ? 'Edit Arth Gurukul - Input Data Sheet' : 'Add New Client'}
          </h1>
          <p className="text-gray-400">Complete client information for financial planning</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2D5A8F] transition-all"
        >
          Cancel
        </button>
      </div>

      {/* Prefill Banner - Shows client submitted data summary */}
      {showPrefillBanner && prefillData?.client_submitted_data && (
        <div className="bg-gradient-to-r from-green-900/30 to-green-800/30 rounded-xl p-4 border border-green-500/50">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <FaUser className="text-green-400" />
              </div>
              <div>
                <h3 className="text-green-400 font-bold">Client Data Loaded</h3>
                <p className="text-gray-300 text-sm">Personal details have been pre-filled from client submission. Review and complete the remaining fields.</p>
                
                {/* Quick Reference Summary */}
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="bg-[#0F172A] rounded-lg p-2">
                    <p className="text-gray-400">Annual Income</p>
                    <p className="text-white font-medium">{formatCurrency(prefillData.client_submitted_data.annual_income)}</p>
                  </div>
                  <div className="bg-[#0F172A] rounded-lg p-2">
                    <p className="text-gray-400">Total Assets</p>
                    <p className="text-green-400 font-medium">{formatCurrency(prefillData.client_submitted_data.total_assets)}</p>
                  </div>
                  <div className="bg-[#0F172A] rounded-lg p-2">
                    <p className="text-gray-400">Total Liabilities</p>
                    <p className="text-red-400 font-medium">{formatCurrency(prefillData.client_submitted_data.total_liabilities)}</p>
                  </div>
                  <div className="bg-[#0F172A] rounded-lg p-2">
                    <p className="text-gray-400">Net Worth</p>
                    <p className="text-[#FFD700] font-medium">{formatCurrency(prefillData.client_submitted_data.net_worth)}</p>
                  </div>
                </div>
              </div>
            </div>
            <button 
              onClick={() => setShowPrefillBanner(false)}
              className="text-gray-400 hover:text-white p-1"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 text-red-200">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h2 className="text-xl font-bold text-[#FFD700] mb-6">👤 Client Profile</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Occupation/Profile
              </label>
              <input
                type="text"
                name="profile_occupation"
                value={formData.profile_occupation}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                placeholder="Software Engineer"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Age
              </label>
              <input
                type="number"
                name="age"
                value={formData.age}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                placeholder="35"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Contact Number
              </label>
              <input
                type="tel"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                placeholder="+91 98765 43210"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Spouse Name
              </label>
              <input
                type="text"
                name="spouse_name"
                value={formData.spouse_name}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                placeholder="Jane Doe"
              />
            </div>
          </div>
        </div>

        {/* Children Details */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[#FFD700]">👶 Children Details</h2>
            <button
              type="button"
              onClick={addChild}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all"
            >
              <FaPlus />
              <span>Add Child</span>
            </button>
          </div>

          {formData.children.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No children added yet. Click "Add Child" to begin.</p>
          ) : (
            <div className="space-y-4">
              {formData.children.map((child, index) => (
                <div key={index} className="bg-[#0A1929] rounded-lg p-4 border border-[#2D5A8F]">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold">Child {index + 1}</h3>
                    <button
                      type="button"
                      onClick={() => removeChild(index)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <FaTimes />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-gray-400 text-xs mb-2">Name</label>
                      <input
                        type="text"
                        value={child.name}
                        onChange={(e) => updateChild(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white text-sm focus:outline-none focus:border-[#FFD700]"
                        placeholder="Child name"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-400 text-xs mb-2">Date of Birth</label>
                      <input
                        type="date"
                        value={child.date_of_birth}
                        onChange={(e) => updateChild(index, 'date_of_birth', e.target.value)}
                        className="w-full px-3 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white text-sm focus:outline-none focus:border-[#FFD700]"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-400 text-xs mb-2">Age</label>
                      <input
                        type="number"
                        value={child.age}
                        readOnly
                        className="w-full px-3 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-gray-400 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-400 text-xs mb-2">Class/Grade</label>
                      <input
                        type="text"
                        value={child.class_grade}
                        onChange={(e) => updateChild(index, 'class_grade', e.target.value)}
                        className="w-full px-3 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white text-sm focus:outline-none focus:border-[#FFD700]"
                        placeholder="Grade 5"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Advisor Branding */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h2 className="text-xl font-bold text-[#FFD700] mb-6">📋 Advisor Information (For PDF Reports)</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Advisor Name
              </label>
              <input
                type="text"
                name="advisor_name"
                value={formData.advisor_name}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                placeholder="Your Name"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Designation
              </label>
              <input
                type="text"
                name="advisor_designation"
                value={formData.advisor_designation}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                placeholder="CFP, CWM"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Company Name
              </label>
              <input
                type="text"
                name="advisor_company"
                value={formData.advisor_company}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                placeholder="Wealth Management Co."
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="advisor_phone"
                value={formData.advisor_phone}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                placeholder="+91 98765 43210"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Email
              </label>
              <input
                type="email"
                name="advisor_email"
                value={formData.advisor_email}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                placeholder="advisor@example.com"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Website
              </label>
              <input
                type="url"
                name="advisor_website"
                value={formData.advisor_website}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                placeholder="www.mywealth.com"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard/clients')}
            className="px-6 py-3 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <FaSpinner className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FaSave />
                <span>{isEdit ? 'Update Client' : 'Save Client'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;